import usePermissions, { Permission } from "./usePermissions";
import { useGetUserDataQuery } from "../auth/dataAccess";
import { switchRole as switchRoleAction } from "../auth/login/loginSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { isUserLoggedIn, LOCAL_STORAGE_USER_DATA_KEY } from "../auth/login/loginSlice";
import { useGetRolesQuery } from "../upload/dataAccess";
import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

type RolesDefinition = Record<Role, Permission[]>;

// TODO implement rest of the roles
export type Role =
    | "Reader"
    | "Adjudicator"
    | "Site User"
    | "Project Manager"
    | "Client Admin"
    | "Sponsor"
    | "CRA"
    | "CRC"
    | "Master Admin";

const rolesDefinition: RolesDefinition = {
    "Reader": ["Review", "Launch Orion", "Download", "Change Status", "Edit", "QC", "Report"],
    "Adjudicator": ["Review", "Launch Orion", "Download", "Change Status", "Edit", "QC", "Report", "Review All"],
    "Site User": ["Upload", "Review"],
    "Project Manager": ["Review", "Launch Orion", "Download", "Change Status", "Delete", "QC", "Report"],
    "Client Admin": ["Upload", "Review", "Launch Orion", "Download", "Change Status", "Delete", "QC"],
    "Sponsor": ["Review", "Launch Orion", "Download", "Report"],
    "CRA": ["Review"],
    "CRC": ["Review"],
    "Master Admin": ["Master Admin"],
};

const useRoles = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { hasPermissions, parsePerMissions } = usePermissions();
    const loggedIn = useAppSelector(isUserLoggedIn);
    const { data: userData } = useGetUserDataQuery(undefined, { skip: !loggedIn });
    const { data: roles } = useGetRolesQuery(userData?.userID ?? "", { skip: !loggedIn || !userData });

    // TODO: This is a workaround to reload other tabs opened by the same user when the user's role is changed
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === LOCAL_STORAGE_USER_DATA_KEY) {
                window.location.reload();
            }
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    const switchRole = async (role: Role) => {
        await dispatch(switchRoleAction({ userId: userData?.userID ?? "", role }));

        history.replace("/patients", { shallow: true });
    };

    const userHasRole = (role: Role): boolean => {
        if (!userData) return false;

        const rolePermissions = rolesDefinition[role];

        const permission = parsePerMissions(userData.adminFeatures);

        if (role === "Master Admin") {
            return permission.includes("Master Admin");
        }

        // Check if user and role permissions are exactly the same
        return hasPermissions(rolePermissions) && permission.length === rolePermissions.length;
    };

    const userActiveRole = useMemo(() => {
        if (!userData) return null;

        const permission = parsePerMissions(userData.adminFeatures);

        const role = Object.keys(rolesDefinition).find((role) => {
            const rolePermissions = rolesDefinition[role as Role];
            return hasPermissions(rolePermissions) && permission.length === rolePermissions.length;
        });

        return role;
    }, [userData]);

    const getRoleLabel = (role: string) => {
        return role
            .split("_")
            .map((word) => word.toLowerCase())
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const getUserRoles = () => {
        if (!roles) return null;

        return roles?.roles;
    };

    return { userHasRole, userActiveRole, switchRole, getUserRoles, getRoleLabel };
};

export default useRoles;
